// frontend/src/components/admin/PromptManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PromptManagement.css';

const PromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  const [formData, setFormData] = useState({
    prompt_text: '',
    notes: ''
  });

  // Auth helper
  const getAuthConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const response = await axios.get('https://aircomply-backend.onrender.com/api/admin/prompts', getAuthConfig());
      // Handle different response formats
      const promptsData = Array.isArray(response.data) ? response.data : (response.data.prompts || []);
      setPrompts(promptsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching prompts:', error);
      setPrompts([]);
      setLoading(false);
    }
  };

  const handleEdit = async (promptId) => {
    try {
      const response = await axios.get(`https://aircomply-backend.onrender.com/api/admin/prompts/${promptId}`, getAuthConfig());
      setSelectedPrompt(response.data);
      setFormData({
        prompt_text: response.data.prompt_text,
        notes: response.data.notes || ''
      });
      setEditMode(true);
      setTestResult(null);
    } catch (error) {
      console.error('Error fetching prompt details:', error);
    }
  };

  const handleSave = async () => {
    if (!selectedPrompt) return;
    
    setSaving(true);
    try {
      await axios.put(`https://aircomply-backend.onrender.com/api/admin/prompts/${selectedPrompt.id}`, formData, getAuthConfig());
      alert('Prompt saved successfully! (Not yet active)');
      fetchPrompts();
      setSaving(false);
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Error saving prompt');
      setSaving(false);
    }
  };

  const handleDeploy = async () => {
    if (!selectedPrompt) return;
    
    if (!window.confirm('Deploy this prompt? It will become active immediately for all users.')) {
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`https://aircomply-backend.onrender.com/api/admin/prompts/${selectedPrompt.id}`, formData, getAuthConfig());
      await axios.post(`/api/admin/prompts/${selectedPrompt.id}/activate`, {}, getAuthConfig());
      alert('Prompt deployed! Now active for all users.');
      fetchPrompts();
      setEditMode(false);
      setSelectedPrompt(null);
      setSaving(false);
    } catch (error) {
      console.error('Error deploying prompt:', error);
      alert('Error deploying prompt');
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!selectedPrompt) return;
    
    const testDoc = window.prompt('Paste sample permit text to test (or leave blank for simple test):');
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`https://aircomply-backend.onrender.com/api/admin/prompts/${selectedPrompt.id}/test`, {
        test_document_text: testDoc || 'This is a test permit document.',
        test_name: 'Manual test'
      }, getAuthConfig());
      
      setTestResult(response.data);
      setTesting(false);
    } catch (error) {
      console.error('Error testing prompt:', error);
      setTestResult({ success: false, error: error.message });
      setTesting(false);
    }
  };

  const handleRollback = async (version) => {
    if (!window.confirm(`Rollback to version ${version}? This will create a new version with the old prompt text.`)) {
      return;
    }
    
    try {
      await axios.post(`/api/admin/prompts/${selectedPrompt.id}/rollback/${version}`, {}, getAuthConfig());
      alert(`Rolled back to version ${version}`);
      fetchPrompts();
      setEditMode(false);
      setSelectedPrompt(null);
    } catch (error) {
      console.error('Error rolling back:', error);
      alert('Error rolling back');
    }
  };

  const getCategoryBadge = (category) => {
    const badges = {
      'document_analysis': { color: '#0066FF', label: 'Document Analysis' },
      'task_extraction': { color: '#10B981', label: 'Task Extraction' },
      'chat': { color: '#F59E0B', label: 'AI Chat' },
      'document_template': { color: '#8B5CF6', label: 'Document Template' }
    };
    
    const badge = badges[category] || { color: '#6B7280', label: category };
    
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        backgroundColor: badge.color,
        color: 'white',
        borderRadius: '12px',
        fontSize: '0.875rem',
        fontWeight: '600'
      }}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return <div className="admin-container"><div className="loading">Loading prompts...</div></div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Prompt Management</h1>
        <p>Edit AI prompts to improve extraction and responses. Changes take effect immediately when deployed.</p>
      </div>

      {!editMode ? (
        <div className="prompts-list">
          {prompts.length === 0 ? (
            <div className="no-prompts">
              <p>No prompts found. Please check database.</p>
            </div>
          ) : (
            prompts.map(prompt => (
              <div key={prompt.id} className="prompt-card">
                <div className="prompt-card-header">
                  <div>
                    <h3>{prompt.name}</h3>
                    {getCategoryBadge(prompt.category)}
                    {prompt.is_active && (
                      <span className="active-badge">Active</span>
                    )}
                  </div>
                  <button 
                    className="btn-edit"
                    onClick={() => handleEdit(prompt.id)}
                  >
                    Edit
                  </button>
                </div>
                <div className="prompt-card-meta">
                  <span>Version: {prompt.version}</span>
                  <span>Updated: {new Date(prompt.updated_at).toLocaleDateString()}</span>
                </div>
                {prompt.notes && (
                  <div className="prompt-notes">
                    <strong>Notes:</strong> {prompt.notes}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="prompt-editor">
          <div className="editor-header">
            <div>
              <h2>{selectedPrompt.name}</h2>
              {getCategoryBadge(selectedPrompt.category)}
              <span className="version-info">Version {selectedPrompt.version}</span>
            </div>
            <button 
              className="btn-close"
              onClick={() => {
                setEditMode(false);
                setSelectedPrompt(null);
                setTestResult(null);
              }}
            >
              ← Back to List
            </button>
          </div>

          <div className="editor-content">
            <div className="form-group">
              <label>Prompt Text:</label>
              <textarea
                className="prompt-textarea"
                value={formData.prompt_text}
                onChange={(e) => setFormData({ ...formData, prompt_text: e.target.value })}
                rows={20}
                placeholder="Enter prompt instructions..."
              />
              <small className="help-text">
                Use placeholders like {'{{documentText}}'} and {'{{facilityInfo}}'} - they'll be replaced automatically.
              </small>
            </div>

            <div className="form-group">
              <label>Notes (What did you change?):</label>
              <textarea
                className="notes-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="e.g., Added PADEP-specific extraction rules"
              />
            </div>

            <div className="editor-actions">
              <button 
                className="btn-test"
                onClick={handleTest}
                disabled={testing}
              >
                {testing ? 'Testing...' : 'Test Prompt'}
              </button>
              <button 
                className="btn-save"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button 
                className="btn-deploy"
                onClick={handleDeploy}
                disabled={saving}
              >
                {saving ? 'Deploying...' : '🚀 Deploy (Go Live)'}
              </button>
            </div>

            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                <h3>Test Result:</h3>
                {testResult.success ? (
                  <pre>{JSON.stringify(testResult.result, null, 2)}</pre>
                ) : (
                  <div className="error-message">
                    <strong>Error:</strong> {testResult.error}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptManagement;
